import type { IQrGrid } from './IQrGrid';
import { qrAlignmentPositions, qrEclBits, qrSize } from './qrTables';

/** Біт `i` числа. */
const bit = (value: number, i: number): boolean => ((value >>> i) & 1) !== 0;

/** Службовий модуль (x — стовпець, y — рядок). */
function setFn(grid: IQrGrid, x: number, y: number, dark: boolean): void {
  grid.modules[y * grid.size + x] = dark;
  grid.fn[y * grid.size + x] = true;
}

/** Шаблон пошуку 7×7 з білою рамкою навколо (центр — x, y). */
function finder(grid: IQrGrid, x: number, y: number): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      const xx = x + dx;
      const yy = y + dy;
      if (xx >= 0 && xx < grid.size && yy >= 0 && yy < grid.size) setFn(grid, xx, yy, dist !== 2 && dist !== 4);
    }
  }
}

/** Шаблон вирівнювання 5×5 (центр — x, y). */
function alignment(grid: IQrGrid, x: number, y: number): void {
  for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) setFn(grid, x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
}

/** Форматне слово (рівень M і маска, BCH(15,5), XOR 0x5412) — дві копії біля шаблонів пошуку і завжди темний модуль. */
export function drawFormatBits(grid: IQrGrid, mask: number): void {
  const data = (qrEclBits << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;
  const n = grid.size;
  for (let i = 0; i <= 5; i++) setFn(grid, 8, i, bit(bits, i));
  setFn(grid, 8, 7, bit(bits, 6));
  setFn(grid, 8, 8, bit(bits, 7));
  setFn(grid, 7, 8, bit(bits, 8));
  for (let i = 9; i < 15; i++) setFn(grid, 14 - i, 8, bit(bits, i));
  for (let i = 0; i < 8; i++) setFn(grid, n - 1 - i, 8, bit(bits, i));
  for (let i = 8; i < 15; i++) setFn(grid, 8, n - 15 + i, bit(bits, i));
  setFn(grid, 8, n - 8, true);
}

/** Слово версії (версії від 7, BCH(18,6)) — два блоки 6×3 біля правого верхнього й лівого нижнього шаблонів пошуку. */
function drawVersion(grid: IQrGrid, version: number): void {
  if (version < 7) return;
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const a = grid.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFn(grid, a, b, bit(bits, i));
    setFn(grid, b, a, bit(bits, i));
  }
}

/**
 * Порожня матриця версії зі службовими модулями: синхронізація, три шаблони пошуку, шаблони вирівнювання (крім кутів із шаблонами пошуку),
 * місце форматного слова (тимчасово з маскою 0) і слово версії.
 */
export function qrBaseGrid(version: number): IQrGrid {
  const size = qrSize(version);
  const grid: IQrGrid = { size, modules: new Array<boolean>(size * size).fill(false), fn: new Array<boolean>(size * size).fill(false) };
  for (let i = 0; i < size; i++) {
    setFn(grid, 6, i, i % 2 === 0);
    setFn(grid, i, 6, i % 2 === 0);
  }
  finder(grid, 3, 3);
  finder(grid, size - 4, 3);
  finder(grid, 3, size - 4);
  const pos = qrAlignmentPositions(version);
  const last = pos.length - 1;
  pos.forEach((y, i) => pos.forEach((x, j) => {
    if (!((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0))) alignment(grid, x, y);
  }));
  drawFormatBits(grid, 0);
  drawVersion(grid, version);
  return grid;
}
